import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import {
    Alert,
    Button,
    Card,
    Col,
    Grid,
    List,
    message,
    Row,
    Space,
    Tooltip,
    Typography,
    Input,
    Tag,
} from 'antd';
import type { GetRef } from 'antd';
import {
    EditOutlined,
    DownOutlined,
    UpOutlined,
    HomeOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useAppDispatch, RootState } from '@store/store.tsx';
import { connsPublic } from '@cfg/axiosConfig.tsx';
import { getCartDrawerItems } from '@eshop/components/cart/cartUtils.tsx';
import { getFormattedPrice } from '@tools/utils/common.tsx';
import { OrderSummaryAccordion } from '@eshop/components/checkout/OrderSummaryAccordion.tsx';
import { ShippingSection } from '@eshop/components/checkout/ShippingSections.tsx';
import { ShippingAddressForm } from '@eshop/components/location/AddressForm.tsx';
import { PaymentMethodSection } from '@eshop/components/checkout/PaymentMethodsSection.tsx';
import { VoucherSection } from '@eshop/pages/cart/Bloques.tsx';
import { ModalBasicInfoUser } from '@eshop/components/users/ModalBasicInfoUser.tsx';
// import GuestLinkPanel from '@eshop/components/users/GuestLinkPanel';
import { RenderTour } from '@eshop/components/tours/renderTour.tsx';
import { checkoutTourSteps } from '@eshop/components/tours/checkoutTours.tsx';
import { useCheckoutFlow } from '@eshop/hooks/useCheckoutFlow.ts';
import { GuestIdentifyStep } from '@eshop/components/checkout/GuestIdentifyStep.tsx';
import { useAssistantPageContext } from '@features/assistant';
import { markGuestPhoneVerified } from '@store/slices/userSlice.tsx';
import { useCheckoutLayout } from '@eshop/hooks/useCheckoutLayout.tsx';
import { selectSelectedPaymentMethod } from '@store/slices/paymentsSlice.tsx';
import { ShippingMethod } from '@eshop/types/shipping.types.ts';
import { CheckoutPreviewContext } from '@eshop/types/checkoutPreview.types.ts';
import { CheckoutStepIndicator, CheckoutStepId } from '@eshop/components/checkout/CheckoutStepIndicator.tsx';
import { DeliveryTypeStep } from '@eshop/components/checkout/DeliveryTypeStep.tsx';
import { CompletedIdentifyCard } from '@eshop/components/checkout/CompletedStepCard.tsx';
import { TakeawayInfo } from '@eshop/components/checkout/TakeawayInfo.tsx';
import { mapApiToShipping } from '@eshop/components/checkout/shippingMapper.ts';
import { getPotentialPaymentPromotionMessages } from '@eshop/utils/potentialPaymentPromotions.ts';
import type { Address } from '@eshop/types/location.types.ts';
import './checkout.a11y.css';
import { trackCheckoutStarted } from '@eshop/utils/itemAnalytics.ts';


const getShippingCode = (method?: ShippingMethod): string => method?.codigo ?? method?.code ?? '';

const isToAgreeMethod = (method?: ShippingMethod): boolean => {
    if (!method) return false;
    return getShippingCode(method) === 'to_agree' || method.strategy === 'manual';
};

const isPickupMethod = (method?: ShippingMethod): boolean => {
    if (!method) return false;
    const code = getShippingCode(method);
    return code === 'takeaway' || code === 'pickup' || method.strategy === 'pickup';
};

const getShippingCost = (method?: ShippingMethod): number => {
    const raw = method?.calculated_cost?.incl_tax;
    return typeof raw === 'number' ? raw : 0;
};

const normalizeStoreAddress = (storeAddress: Address[] | Address | null | undefined): Address | null => {
    if (Array.isArray(storeAddress)) {
        return storeAddress[0] ?? null;
    }
    return storeAddress ?? null;
};


// Componente principal
export const CheckoutPage: React.FC = () => {
    useAssistantPageContext({
        area: 'eshop',
        module: 'checkout',
        screen: 'checkout',
        mode: 'checkout',
    });

    // Estados
    const user = useSelector((state: RootState) => state.user.profile);
    const initialized = useSelector((state: RootState) => state.user.profile);
    const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
    const selectedPaymentMethod = useSelector(selectSelectedPaymentMethod);
    const dispatch = useAppDispatch();

    // Mobile detection for conditional feedback
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md; // < 768px

    const cart = useSelector((state: RootState) => state.cart);
    const siteSettings = useSelector((state: RootState) => state.settings.site);
    const [currency, setCurrency] = useState<string | undefined>();
    const [openPopConfirmItemDelete, setOpenPopConfirmItemDelete] = useState<number | null>(null);
    const [shippingMethodSummary, setShippingMethodSummary] = useState<ShippingMethod | undefined>();
    const [arrangeShippingMethod, setArrangeShippingMethod] = useState<ShippingMethod | undefined>();
    const [hasPublicPickup, setHasPublicPickup] = useState(false);

    const [headerHeight, setHeaderHeight] = useState(0);

    useEffect(() => {
        const updateHeaderHeight = () => {
            const headerElement = document.querySelector('.eshop-header');
            if (headerElement) {
                setHeaderHeight(headerElement.getBoundingClientRect().height);
            } else {
                setHeaderHeight(0);
            }
        };

        updateHeaderHeight();
        window.addEventListener('resize', updateHeaderHeight);
        window.addEventListener('load', updateHeaderHeight);

        const interval = setInterval(updateHeaderHeight, 500);

        return () => {
            window.removeEventListener('resize', updateHeaderHeight);
            window.removeEventListener('load', updateHeaderHeight);
            clearInterval(interval);
        };
    }, []);

    const shippingMethodsFocusRef = useRef<HTMLDivElement>(null);
    const paymentMethodsFocusRef = useRef<HTMLDivElement>(null);
    const commentFieldRef = useRef<GetRef<typeof Input.TextArea>>(null);

    const focusShippingMethods = useCallback(() => {
        shippingMethodsFocusRef.current?.focus();
    }, []);

    const focusPaymentMethods = useCallback(() => {
        paymentMethodsFocusRef.current?.focus();
    }, []);

    const focusCommentField = useCallback(() => {
        commentFieldRef.current?.focus();
    }, []);

    const {
        state,
        setTakeaway,
        setAddress,
        setShippingMethod,
        setPaymentMethod,
        previewOrder,
        buildPayload,
        isCheckoutReady,
        getMissingSteps,
        setComment,
        allowGuestCheckout,
        requireGuestIdentify,
        isGuestVerified,
        takeawayAvailability,
        setDeliveryType,
        saveCheckoutSnapshot,
        restoreCheckoutSnapshot,
        contactVerificationRequired,
        retryTotals,
    } = useCheckoutFlow();
    const { enterCheckoutGuard, leaveCheckoutGuard } = useCheckoutLayout();
    const pickupStoreAddress = useMemo(
        () => normalizeStoreAddress(cart.storeAddress ?? siteSettings?.store_address),
        [cart.storeAddress, siteSettings?.store_address],
    );

    useEffect(() => {
        let isMounted = true;

        const fetchDeliveryMethods = async () => {
            try {
                const response = await connsPublic<unknown[]>('eshop:shipping:active_list').get();
                if (!isMounted) return;
                const rawMethods = Array.isArray(response.data) ? response.data : [];
                const publicMethods = rawMethods.map(mapApiToShipping);
                setArrangeShippingMethod(publicMethods.find(isToAgreeMethod));
                setHasPublicPickup(publicMethods.some(isPickupMethod));
            } catch {
                if (isMounted) {
                    setArrangeShippingMethod(undefined);
                    setHasPublicPickup(false);
                }
            }
        };

        void fetchDeliveryMethods();
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (cart.hasLoaded && cart.cartItems.length > 0) {
            trackCheckoutStarted(cart.id, cart.cartItems, cart.total.total, currency || 'ARS');
        }
    }, [cart.hasLoaded, cart.id, cart.cartItems, cart.total.total, currency]);

    useEffect(() => {
        if (state.deliveryType !== 'arrange' || !arrangeShippingMethod) {
            return;
        }
        if (state.shippingMethod !== arrangeShippingMethod.id) {
            setShippingMethod(arrangeShippingMethod.id, getShippingCost(arrangeShippingMethod));
        }
        setShippingMethodSummary((current) =>
            current?.id === arrangeShippingMethod.id ? current : arrangeShippingMethod
        );
    }, [arrangeShippingMethod, setShippingMethod, state.deliveryType, state.shippingMethod]);

    // Estado para controlar el modal de datos de usuario
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    useEffect(() => {
        if (!isAuthenticated) {
            setIsUserModalOpen(false);
            return;
        }
        const needsContact = !user?.phone || !user?.email;
        setIsUserModalOpen(needsContact);
    }, [isAuthenticated, user?.phone, user?.email]);

    // Accordion State
    const [expandedBlock, setExpandedBlock] = useState<'delivery' | 'payment' | 'none'>('delivery');
    const snapshotInitRef = useRef(false);

    useEffect(() => {
        if (snapshotInitRef.current) {
            return;
        }
        snapshotInitRef.current = true;
        const restored = restoreCheckoutSnapshot();
        if (restored) {
            setExpandedBlock('payment');
        }
    }, [restoreCheckoutSnapshot]);


    const handleShippingMethodChange = ({ id, cost }: { id: number | undefined; cost: number }) => {
        setShippingMethod(id, cost);
    };

    const handlePaymentMethodChange = (value: number | undefined) => {
        setPaymentMethod(value);
    };

    const handleDeliveryTypeChange = useCallback((type: 'shipping' | 'takeaway' | 'arrange') => {
        setDeliveryType(type);

        if (type === 'takeaway') {
            setTakeaway(true);
            setAddress(undefined);
            setShippingMethod(undefined, 0);
            setShippingMethodSummary(undefined);
            return;
        }

        setTakeaway(false);

        if (type === 'arrange') {
            setAddress(undefined);
            if (arrangeShippingMethod) {
                setShippingMethod(arrangeShippingMethod.id, getShippingCost(arrangeShippingMethod));
                setShippingMethodSummary(arrangeShippingMethod);
            } else {
                setShippingMethod(undefined, 0);
                setShippingMethodSummary(undefined);
            }
            return;
        }

        if (state.deliveryType !== 'shipping') {
            setShippingMethod(undefined, 0);
            setShippingMethodSummary(undefined);
        }
    }, [
        arrangeShippingMethod,
        setAddress,
        setDeliveryType,
        setShippingMethod,
        setTakeaway,
        state.deliveryType,
    ]);

    useEffect(() => {
        enterCheckoutGuard();
        return () => {
            leaveCheckoutGuard();
        };
    }, [enterCheckoutGuard, leaveCheckoutGuard]);

    const navigate = useNavigate();
    const location = useLocation();
    const checkoutPath = useMemo(() => {
        const checkoutIndex = location.pathname.indexOf('/checkout');
        if (checkoutIndex === -1) {
            return '/eshop/checkout';
        }
        return location.pathname.slice(0, checkoutIndex + '/checkout'.length);
    }, [location.pathname]);

    const handleLoginRedirect = () => {
        navigate(`/auth/login?next=${encodeURIComponent(checkoutPath)}`);
    };

    const needsGuestIdentify = requireGuestIdentify();
    const showGuestIdentifyStep = !isAuthenticated && allowGuestCheckout;
    const contactVerified = useMemo(() => isGuestVerified() || state.contactStatus === 'ready_unverified', [isGuestVerified, state.contactStatus]);
    const [guestBlockedReason, setGuestBlockedReason] = useState<string | null>(null);

    const checkoutCartItems = useMemo(() => {
        const totalsLines = state.totals.lines ?? [];
        if (!state.paymentMethod || totalsLines.length === 0) {
            return cart.cartItems;
        }
        const lineTotalsById = new Map(totalsLines.map((line) => [line.line_id, line]));
        return cart.cartItems.map((item) => {
            const totalsLine = lineTotalsById.get(item.id);
            if (!totalsLine) {
                return item;
            }
            return {
                ...item,
                subtotal: {
                    totalSinDesc: totalsLine.subtotal,
                    descuento: totalsLine.discount,
                    total: totalsLine.total,
                },
                discount_labels: totalsLine.discount_labels ?? item.discount_labels,
            };
        });
    }, [cart.cartItems, state.paymentMethod, state.totals.lines]);

    const cartDrawerItems = useMemo(
        () => getCartDrawerItems(
            checkoutCartItems,
            currency,
            setCurrency,
            openPopConfirmItemDelete,
            setOpenPopConfirmItemDelete,
        ),
        [checkoutCartItems, currency, openPopConfirmItemDelete, setCurrency, setOpenPopConfirmItemDelete],
    );

    const totalItemsCount = useMemo(
        () => cart.cartItems.reduce((sum, item) => sum + (item.quantity ?? 0), 0),
        [cart.cartItems],
    );

    const totalsDisplay = useMemo(() => {
        const totals = state.totals;
        const hasTotals = [
            totals.total_previo,
            totals.total_post,
            totals.descuento,
            totals.order_discount,
            totals.shipping,
            totals.shipping_before,
            totals.shipping_discount,
        ].some((value) => Math.abs(value ?? 0) > 0);

        if (!hasTotals && cart.hasLoaded) {
            const fallbackShipping = state.takeaway ? 0 : state.shippingCost;
            return {
                subtotal: cart.total.totalSinDesc,
                lineDiscount: cart.total.descuento,
                orderDiscount: 0,
                orderDiscountLabels: [],
                shipping: fallbackShipping,
                shippingDiscount: 0,
                shippingDiscountLabels: [],
                total: cart.total.total + fallbackShipping,
            };
        }

        return {
            subtotal: cart.total.totalSinDesc,
            lineDiscount: totals.descuento ?? cart.total.descuento,
            orderDiscount: totals.order_discount ?? 0,
            orderDiscountLabels: totals.order_discount_labels ?? [],
            shipping: state.takeaway
                ? 0
                : (totals.shipping ?? totals.shipping_before ?? state.shippingCost),
            shippingDiscount: totals.shipping_discount ?? 0,
            shippingDiscountLabels: totals.shipping_discount_labels ?? [],
            total: totals.total_post ?? 0,
        };
    }, [cart.hasLoaded, cart.total.totalSinDesc, cart.total.descuento, cart.total.total, state.shippingCost, state.takeaway, state.totals]);

    const potentialPaymentPromotions = useMemo(
        () => getPotentialPaymentPromotionMessages(cart.cartItems, state.paymentMethod),
        [cart.cartItems, state.paymentMethod],
    );

    // Compute active step and completed steps for indicator
    const activeStep: CheckoutStepId = showGuestIdentifyStep && !contactVerified
        ? 'identify'
        : state.deliveryType
            ? state.paymentMethod
                ? 'review'
                : 'payment'
            : 'delivery';

    const completedSteps: CheckoutStepId[] = useMemo(() => {
        const completed: CheckoutStepId[] = [];
        if (!showGuestIdentifyStep || contactVerified) completed.push('identify');
        if (state.deliveryType) completed.push('delivery');
        if (state.paymentMethod) completed.push('payment');
        return completed;
    }, [showGuestIdentifyStep, contactVerified, state.deliveryType, state.paymentMethod]);

    // Auto-collapse logic REMOVED as per user request (Manual "Next" button instead)
    // useEffect(() => {
    //     if (activeStep === 'payment' || activeStep === 'review') {
    //         setExpandedBlock('payment');
    //     } else if (activeStep === 'delivery') {
    //         setExpandedBlock('delivery');
    //     }
    // }, [activeStep]);

    const openPreviewPage = async () => {
        const payload = buildPayload();
        const data = await previewOrder(payload);

        // Save checkout state so 'Volver a editar' restores selections
        saveCheckoutSnapshot();

        const shippingKind = payload.shipping_method ?? (payload.takeaway ? 'pickup' : 'delivery');

        const previewContext: CheckoutPreviewContext = {
            shipping_method: shippingKind,
            takeaway: shippingKind === 'pickup',
            shippingAddress: shippingKind === 'delivery' ? payload.shippingAddr ?? state.address ?? null : null,
            branch: shippingKind === 'pickup' ? state.branch ?? pickupStoreAddress ?? null : null,
            shippingMethod:
                shippingKind === 'pickup' || !shippingMethodSummary
                    ? undefined
                    : {
                        id: shippingMethodSummary.id,
                        name: shippingMethodSummary.name,
                        description: shippingMethodSummary.descripcion ?? null,
                        imageUrl: shippingMethodSummary.image?.default ?? null,
                        cost:
                            shippingMethodSummary.calculated_cost?.incl_tax ?? state.shippingCost ?? null,
                    },
            paymentMethod: selectedPaymentMethod
                ? {
                    id: selectedPaymentMethod.id,
                    name: selectedPaymentMethod.name,
                    instructions: selectedPaymentMethod.description ?? null,
                    imageUrl: selectedPaymentMethod.image?.thumbnails?.default ?? null,
                }
                : undefined,
            comment: state.comment ?? payload.comment ?? null,
            contactEmail: payload.email ?? state.user?.email ?? null,
            contactPhone: payload.whatsapp ?? state.user?.phone ?? null,
            customerName: (() => {
                const addressSource = payload.shippingAddr ?? state.address;
                if (addressSource) {
                    const first = addressSource.first_name ?? '';
                    const last = addressSource.last_name ?? '';
                    const full = `${first} ${last}`.trim();
                    if (full.length > 0) {
                        return full;
                    }
                }
                const profileFirst = user?.first_name ?? '';
                const profileLast = user?.last_name ?? '';
                const profileFull = `${profileFirst} ${profileLast}`.trim();
                return profileFull.length > 0 ? profileFull : null;
            })(),
        };

        navigate(`${checkoutPath}/preview/${data.preview_hash}`, {
            state: { preview: data, payload, checkoutContext: previewContext },
        });
    };

    useEffect(() => {
        if (!needsGuestIdentify) {
            return;
        }
        const restrictedPaths = ['shipping', 'payment', 'review'].map(
            (segment) => `${checkoutPath}/${segment}`,
        );
        if (restrictedPaths.includes(location.pathname)) {
            navigate(checkoutPath, { replace: true });
        }
    }, [checkoutPath, location.pathname, navigate, needsGuestIdentify]);

    useEffect(() => {
        if (contactVerified) {
            setGuestBlockedReason(null);
        }
    }, [contactVerified]);

    useEffect(() => {
        if (state.takeaway) {
            setShippingMethodSummary(undefined);
        }
    }, [state.takeaway]);


    const handlePlaceOrderClick = async () => {
        if (!isAuthenticated && !allowGuestCheckout) {
            message.warning('Necesitás iniciar sesión para finalizar tu compra.');
            handleLoginRedirect();
            return;
        }
        const missing = getMissingSteps();
        if (missing.length > 0) {
            message.warning({
                content: missing.map((m, i) => `${i + 1}. ${m}`).join('\n'),
                duration: 5,
            });
            return;
        }
        try {
            await openPreviewPage();
        } catch (error) {
            const detail =
                (error as { response?: { data?: { detail?: unknown; message?: string } } })?.response?.data?.detail ??
                (error as { response?: { data?: unknown } })?.response?.data;
            const detailObject =
                typeof detail === 'string'
                    ? { message: detail }
                    : (detail as { code?: string; message?: string } | undefined);

            if (!isAuthenticated && detailObject?.code === 'guest_phone_unverified') {
                const warningMessage =
                    detailObject.message ??
                    'Necesitamos verificar tu WhatsApp antes de continuar. Volvé a solicitar un código.';
                setGuestBlockedReason(warningMessage);
                dispatch(markGuestPhoneVerified(null));
                message.warning(warningMessage);
                return;
            }

            if (detailObject?.code && detailObject.code.startsWith('takeaway_')) {
                const fallbackMessage =
                    detailObject.message ?? 'El retiro en tienda no está disponible en este horario.';
                message.warning(fallbackMessage);
                setTakeaway(false);
                return;
            }

            const fallback = detailObject?.message ?? 'No pudimos previsualizar tu pedido. Intentá nuevamente.';
            message.error(fallback);
        }
    };

    const renderSummaryDetails = ({ showVoucher = false }: { showVoucher?: boolean } = {}) => (
        <div className="checkout-summary-details">
            {showVoucher && (
                <div className="pt-2 mt-2 w_300px ml-auto ms-auto">
                    <VoucherSection />
                </div>
            )}

            {potentialPaymentPromotions.slice(0, 2).map((promotion) => (
                <Alert
                    key={promotion.key}
                    type="info"
                    showIcon
                    message={promotion.text}
                    className="mt-2 mb-2"
                    style={{ fontSize: 12, padding: '6px 10px' }}
                />
            ))}

            <div className="pt-2 mt-2">
                <div className="checkout-summary">
                    {totalsDisplay.subtotal > 0 && (
                        <div className="checkout-summary-row">
                            <Typography.Text>Sub-Total</Typography.Text>
                            <Typography.Text>
                                {getFormattedPrice(totalsDisplay.subtotal, currency)}
                            </Typography.Text>
                        </div>
                    )}
                    {totalsDisplay.lineDiscount > 0 && (
                        <div className="checkout-summary-row">
                            <Typography.Text>Descuentos de línea</Typography.Text>
                            <Typography.Text>
                                – {getFormattedPrice(totalsDisplay.lineDiscount, currency)}
                            </Typography.Text>
                        </div>
                    )}
                    {totalsDisplay.orderDiscount && totalsDisplay.orderDiscount > 0 ? (
                        <div className="checkout-summary-row">
                            <Typography.Text>Descuento de orden</Typography.Text>
                            <Typography.Text>
                                – {getFormattedPrice(totalsDisplay.orderDiscount, currency)}
                                {totalsDisplay.orderDiscountLabels.map((label) => (
                                    <Tag key={label} color="red" className="ms-1 text-wrap">
                                        {label}
                                    </Tag>
                                ))}
                            </Typography.Text>
                        </div>
                    ) : null}
                    {!state.takeaway && totalsDisplay.shipping > 0 && (
                        <div className="checkout-summary-row">
                            <Typography.Text>Envío</Typography.Text>
                            <Typography.Text>
                                {getFormattedPrice(totalsDisplay.shipping, currency)}
                            </Typography.Text>
                        </div>
                    )}
                    {!state.takeaway && totalsDisplay.shippingDiscount > 0 ? (
                        <div className="checkout-summary-row checkout-summary-row--discount">
                            <Typography.Text>Descuento envío</Typography.Text>
                            <Typography.Text className="text-danger">
                                – {getFormattedPrice(totalsDisplay.shippingDiscount, currency)}
                                {totalsDisplay.shippingDiscountLabels.map((label) => (
                                    <Tag key={label} color="red" className="ms-1 text-wrap">
                                        {label}
                                    </Tag>
                                ))}
                            </Typography.Text>
                        </div>
                    ) : null}
                    <div className="checkout-summary-row checkout-summary-row--total">
                        <Typography.Text strong>Total a pagar</Typography.Text>
                        <Typography.Text strong>
                            {(state.totalsStatus === 'ready' ? getFormattedPrice(totalsDisplay.total, currency) : 'Pendiente de cotización')}
                        </Typography.Text>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderOrderSummaryCard = ({
        showItems,
        showCta,
    }: {
        showItems: boolean;
        showCta: boolean;
    }) => (
        <Card title={
            <div className="checkout-summary-card-header">
                <div>Resumen del Pedido</div>
                {(user?.email || user?.phone) && (
                    <div className="checkout-summary-card-contact">
                        {user.email} {user.phone && `| ${user.phone}`}
                    </div>
                )}
            </div>
        } style={{ minWidth: 350 }}>
            {showItems && (
                <List
                    itemLayout="horizontal"
                    dataSource={cartDrawerItems}
                    renderItem={(item) => (
                        <List.Item key={item.key}>
                            <List.Item.Meta
                                avatar={item.imageElement}
                                title={item.title}
                                description={item.description}
                            />
                        </List.Item>
                    )}
                />
            )}
            {renderSummaryDetails({ showVoucher: true })}

            {showCta && (
                <div className="checkout-summary-cta">
                    <Tooltip
                        title={
                            !isCheckoutReady()
                                ? (
                                    <ul style={{ margin: 0, padding: '0 1rem' }}>
                                        {getMissingSteps().map((msg, i) => (
                                            <li key={i}>{msg}</li>
                                        ))}
                                    </ul>
                                )
                                : ''
                        }
                        placement="top"
                    >
                        <span>
                            <Button
                                size="large"
                                block
                                disabled={!isCheckoutReady()}
                                className={`paymentBtnClass rounded-pill px-3 hover_border_0 ${isCheckoutReady() ? 'hover_info bg_verde_AV text-black' : ''}`}
                                onClick={handlePlaceOrderClick}
                            >
                                Confirmar pedido
                            </Button>
                        </span>
                    </Tooltip>
                    {!isCheckoutReady() && (
                        <div className="checkout-cta-helper-text" role="status">
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                Te faltan: {getMissingSteps().join(', ')}
                            </Typography.Text>
                        </div>
                    )}
                </div>
            )}
        </Card>
    );

    // Sección 1: Entrega (Siempre visible, expandida si no hay método de pago seleccionado o si se está editando)
    const renderDeliveryBlock = () => {
        const isDeliveryComplete = Boolean(state.deliveryType) &&
            (state.deliveryType === 'takeaway'
                ? true
                : state.deliveryType === 'arrange'
                    ? Boolean(state.shippingMethod)
                    : Boolean(state.address && state.shippingMethod));

        const isExpanded = expandedBlock === 'delivery';

        const toggleExpand = () => {
            setExpandedBlock(prev => prev === 'delivery' ? 'none' : 'delivery');
        };

        const summaryText = state.deliveryType === 'takeaway'
            ? `Retiro en Tienda`
            : state.deliveryType === 'arrange'
                ? `Entrega a coordinar${shippingMethodSummary ? ` por ${shippingMethodSummary.name}` : ''}`
                : `Envío a ${state.address?.street || 'Domicilio'} ${shippingMethodSummary ? `por ${shippingMethodSummary.name}` : ''}`;

        return (
            <Card
                className={`mb-3 ${isDeliveryComplete ? 'border-success' : ''}`}
                title={
                    <div
                        className="w-100 cursor-pointer"
                        onClick={toggleExpand}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className="d-flex justify-content-between align-items-center">
                            <Space>
                                <span>1. Entrega</span>
                                {isDeliveryComplete && <Tag color="success">Completado</Tag>}
                            </Space>
                            {isExpanded ? <UpOutlined style={{ fontSize: '12px' }} /> : <DownOutlined style={{ fontSize: '12px' }} />}
                        </div>
                        {!isExpanded && isDeliveryComplete && (
                            <div className="mt-2 small" style={{ color: 'var(--text-secondary)' }}>
                                <Space size={4} align="center" wrap>
                                    <span className="fw-bold">{summaryText}</span>
                                    <Button type="link" icon={<EditOutlined />} size="small" className="p-0 ms-2" onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedBlock('delivery');
                                    }} />
                                </Space>
                            </div>
                        )}
                    </div>
                }
                styles={{
                    root: { minHeight: 80 },
                    body: { display: isExpanded ? 'block' : 'none' },
                    header: !isExpanded && isDeliveryComplete ? { backgroundColor: 'var(--surface-panel)', color: 'var(--text-default)' } : undefined
                }}
            >
                <DeliveryTypeStep
                    value={state.deliveryType ?? undefined}
                    onChange={handleDeliveryTypeChange}
                    hasShipping={cart.hasShipping || (!cart.hasShipping && !cart.hasTakeaway)}
                    hasTakeaway={(cart.hasTakeaway || hasPublicPickup || (!cart.hasShipping && !cart.hasTakeaway)) && takeawayAvailability.available}
                    hasArrange={Boolean(arrangeShippingMethod)}
                />

                {!cart.hasShipping && !cart.hasTakeaway && (
                    <Alert
                        type="warning"
                        showIcon
                        title="Modo Debug: El carrito no reporta métodos de entrega disponibles."
                        className="mt-3"
                    />
                )}

                {state.deliveryType === 'takeaway' && (
                    <div className="mt-3">
                        <TakeawayInfo
                            storeAddress={pickupStoreAddress}
                            whatsappNumber={user?.phone} // Fallback to user phone if generic whatsapp not avail
                        />
                    </div>
                )}

                {state.deliveryType === 'arrange' && (
                    <div className="mt-3">
                        <Alert
                            type="info"
                            showIcon
                            title="Entrega a coordinar"
                            description={arrangeShippingMethod?.descripcion ?? 'Coordinaremos la entrega con los datos de contacto del pedido.'}
                        />
                    </div>
                )}

                {state.deliveryType === 'shipping' && (
                    <div className="mt-3">
                        <ShippingAddressForm
                            className="shippingAddressFormClass"
                            onChange={setAddress}
                            onAutoSelectNext={focusShippingMethods}
                            initialAddressId={(() => {
                                if (!state.address) return undefined;
                                const guestId = (state.address as any).guestAddressId;
                                return guestId !== undefined ? Number(guestId) : state.address.id;
                            })()}
                        />
                        {state.address && (
                            <div
                                className="mt-3"
                                ref={shippingMethodsFocusRef}
                                tabIndex={-1}
                                style={{ outline: 'none' }}
                            >
                                <ShippingSection
                                    className="shippingSectionClass"
                                    onShippingMethodChange={handleShippingMethodChange}
                                    selectedAddress={state.address}
                                    cartItems={cart.cartItems}
                                    totalCart={cart.total.total}
                                    onAutoSelectNext={focusPaymentMethods}
                                    onSelectedShippingMethodChange={setShippingMethodSummary}
                                    id={state.shippingMethod}
                                />
                            </div>
                        )}
                    </div>
                )}

                {isExpanded && isDeliveryComplete && (
                    <div className="mt-4 d-flex justify-content-end">
                        <Button
                            type="primary"
                            onClick={() => setExpandedBlock('payment')}
                            className="rounded-pill px-4"
                        >
                            Continuar con el Pago
                        </Button>
                    </div>
                )}
            </Card>
        );
    };

    // Sección 2: Pago (Visible si Entrega está completa)
    const renderPaymentBlock = () => {
        const isDeliveryComplete = Boolean(state.deliveryType) &&
            (state.deliveryType === 'takeaway'
                ? true
                : state.deliveryType === 'arrange'
                    ? Boolean(state.shippingMethod)
                    : Boolean(state.address && state.shippingMethod));
        const isPaymentComplete = !!state.paymentMethod;
        const isExpanded = expandedBlock === 'payment';

        const toggleExpand = () => {
            if (isDeliveryComplete) {
                setExpandedBlock((prev) => (prev === 'payment' ? 'none' : 'payment'));
            }
        };

        if (!isDeliveryComplete) {
            return (
                <Card className="mb-3 bg-secondary border-default disabled-block opacity-50" variant="borderless">
                    <div className="d-flex align-items-center text-muted">
                        <span className="mr-2 border rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: 24, height: 24 }}>2</span>
                        <span className="fw-bold">Pago</span>
                        <span className="ml-auto small">Completá la entrega primero</span>
                    </div>
                </Card>
            );
        }

        return (
          <Card
            className={`mb-3 ${isPaymentComplete ? 'border-success' : ''}`}
            title={
              <div
                className="w-100 cursor-pointer"
                onClick={toggleExpand}
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <Space>
                    <span>2. Pago</span>
                    {isPaymentComplete && <Tag color="success">Completado</Tag>}
                  </Space>
                  {isExpanded ? (
                    <UpOutlined style={{ fontSize: '12px' }} />
                  ) : (
                    <DownOutlined style={{ fontSize: '12px' }} />
                  )}
                </div>
                {!isExpanded && isPaymentComplete && (
                  <div
                    className="mt-2 small"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    <Space size={4} align="center">
                      <span className="fw-bold">
                        {selectedPaymentMethod?.name}
                      </span>
                      <Button
                        type="link"
                        icon={<EditOutlined />}
                        size="small"
                        className="p-0 ms-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedBlock('payment');
                        }}
                      />
                    </Space>
                  </div>
                )}
              </div>
            }
            styles={{
              root: { minHeight: 80 },
              body: { display: isExpanded ? 'block' : 'none' },
              header:
                !isExpanded && isPaymentComplete
                  ? {
                      backgroundColor: 'var(--surface-panel)',
                      color: 'var(--text-default)',
                    }
                  : undefined,
            }}
          >
            <div
              ref={paymentMethodsFocusRef}
              tabIndex={-1}
              style={{ outline: 'none' }}
            >
              <PaymentMethodSection
                shippingMethod={state.shippingMethod}
                useShipping={!state.takeaway}
                selectedPaymentMethodId={state.paymentMethod}
                onPaymentMethodChange={handlePaymentMethodChange}
                className="paymentMethodSectionClass"
                onAutoSelectNext={focusCommentField}
              />
            </div>
            <Input.TextArea
              className="mt-4"
              value={state.comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Comentario opcional para el pedido"
              rows={2}
              ref={commentFieldRef}
            />
          </Card>
        );
    };

    const renderMainContent = () => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                {state.totalsStatus === 'error' && (
                    <Alert type="error" title="No pudimos calcular el total" description="Tus datos y selecciones se conservaron." action={<Button onClick={retryTotals}>Reintentar cotización</Button>} showIcon />
                )}
                {state.totalsStatus === 'loading' && <Typography.Text role="status">Calculando el total…</Typography.Text>}
                {/* Identification bar */}
                {showGuestIdentifyStep && contactVerified && (
                    <CompletedIdentifyCard
                        email={state.user?.email ?? undefined}
                        phone={state.user?.phone ?? undefined}
                        onEdit={() => setIsUserModalOpen(true)}
                    />
                )}

                {/* Mobile Summary Accordion */}
                {isMobile && (
                    <OrderSummaryAccordion
                        items={cartDrawerItems}
                        itemCount={totalItemsCount}
                        totalLabel="Total"
                        totalValue={(state.totalsStatus === 'ready' ? getFormattedPrice(totalsDisplay.total, currency) : 'Pendiente de cotización')}
                        style={{
                            position: 'sticky',
                            top: `${headerHeight}px`,
                            zIndex: 900,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                            borderRadius: '12px',
                        }}
                    >
                        {renderSummaryDetails({ showVoucher: true })}
                    </OrderSummaryAccordion>
                )}

                {renderDeliveryBlock()}
                {renderPaymentBlock()}

                {/* Mobile Sticky Footer Content */}
                {isMobile && (
                    <div className="checkout-sticky-footer" role="region" aria-label="Confirmar pedido">
                        <div className="checkout-sticky-footer__content">
                            <div className="checkout-sticky-footer__total">
                                <Typography.Text type="secondary">Total</Typography.Text>
                                <Typography.Text strong>
                                    {(state.totalsStatus === 'ready' ? getFormattedPrice(totalsDisplay.total, currency) : 'Pendiente de cotización')}
                                </Typography.Text>
                            </div>
                            <Tooltip
                                title={!isCheckoutReady() ? getMissingSteps().join(', ') : ''}
                                trigger={['click', 'hover']}
                            >
                                <Button
                                    size="large"
                                    block
                                    disabled={!isCheckoutReady()}
                                    className={`paymentBtnClass rounded-pill px-3 hover_border_0 ${isCheckoutReady() ? 'hover_info bg_verde_AV text-black' : ''}`}
                                    onClick={handlePlaceOrderClick}
                                >
                                    Confirmar Pedido
                                </Button>
                            </Tooltip>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderCheckoutContent = () => {
        return (
            <Row gutter={24} className="checkout-layout-grid mx-auto mx-lg-0">
                <Col xs={24} lg={12} xl={14}>
                    {renderMainContent()}
                </Col>

                {/* Desktop Sidebar */}
                {!isMobile && (
                    <Col xs={24} lg={12} xl={10}>
                        <div className="checkout-desktop-sidebar position-sticky" style={{ top: '2rem' }}>
                            {renderOrderSummaryCard({ showItems: true, showCta: true })}
                        </div>
                    </Col>
                )}
            </Row>
        );
    };

    return (
        <div className={`checkout-page p-4 w-max-xxl mx-auto${isMobile ? ' checkout-page--has-sticky' : ''}`}>
            <div className="d-flex align-items-center mb-4 gap-2">
                <Button
                    type="text"
                    icon={<HomeOutlined style={{ fontSize: '20px' }} />}
                    onClick={() => navigate('/eshop/catalogue')}
                    title="Volver al catálogo"
                    aria-label="Volver al catálogo"
                    className="checkout-back-home-btn"
                />
                <Typography.Title level={2} style={{ margin: 0 }}>Checkout</Typography.Title>
            </div>
            <RenderTour tourSteps={checkoutTourSteps} tourName={'Tour por Checkout'} />

            {!isAuthenticated && !allowGuestCheckout ? (
                <Alert
                    type="warning"
                    showIcon
                    title="El checkout para invitados está deshabilitado"
                    description="Iniciá sesión o registrate para finalizar tu compra."
                    action={(
                        <Button size="small" type="primary" onClick={handleLoginRedirect}>
                            Iniciar sesión
                        </Button>
                    )}
                    className="mb-4"
                />
            ) : null}

            {/* Usar el modal */}
            {isAuthenticated && user && (!user.phone || !user.email) && initialized &&
                <ModalBasicInfoUser
                    isModalOpen={isUserModalOpen}
                    setIsModalOpen={setIsUserModalOpen}
                />}

            {showGuestIdentifyStep ? (
                <>
                    <CheckoutStepIndicator
                        currentStep={activeStep}
                        completedSteps={completedSteps}
                    />
                    {needsGuestIdentify ? (
                        <GuestIdentifyStep
                            defaultEmail={state.user?.email ?? ''}
                            defaultPhone={state.user?.phone ?? ''}
                            checkoutPath={checkoutPath}
                            blockedReason={guestBlockedReason ?? undefined}
                            requiresVerification={contactVerificationRequired}
                        />
                    ) : (
                        renderCheckoutContent()
                    )}
                </>
            ) : (
                <>
                    <CheckoutStepIndicator
                        currentStep={activeStep}
                        completedSteps={completedSteps}
                    />
                    {renderCheckoutContent()}
                </>
            )}
        </div>
    );
};
